'use client'
import React, { useState, useEffect, useRef } from 'react'
import { useRouter, useSearchParams, usePathname } from 'next/navigation'
import LargePropertyCard from '@components/LargePropertyCard'
import LoadingCard from '@components/LoadingCard'
import { getAgentProperties } from '@services/getAgentProperties'
import { useSession } from 'next-auth/react'

// ── RTL helpers ───────────────────────────────────────────────────────────────
const RTL_LANGS = new Set(['he', 'ar', 'fa', 'ur'])
const prevArrow = (lang) => RTL_LANGS.has(lang) ? '→' : '←'
const nextArrow = (lang) => RTL_LANGS.has(lang) ? '←' : '→'

// ── UI label map ──────────────────────────────────────────────────────────────
const UI_LABELS = {
  all: {
    en: 'All',       th: 'ทั้งหมด',   cn: '全部',      ru: 'Все',          de: 'Alle',       fr: 'Tous',
    it: 'Tutti',     es: 'Todos',     tr: 'Tümü',      ja: 'すべて',        ko: '전체',        ar: 'الكل',
    he: 'הכל',       pt: 'Todos',     nl: 'Alle',      pl: 'Wszystkie',    vi: 'Tất cả',
  },
  forSale: {
    en: 'For Sale',  th: 'ขาย',       cn: '出售',      ru: 'Продажа',      de: 'Kaufen',     fr: 'À vendre',
    it: 'In vendita', es: 'En venta', tr: 'Satılık',   ja: '売却',          ko: '매매',        ar: 'للبيع',
    he: 'למכירה',    pt: 'À venda',   nl: 'Te koop',   pl: 'Na sprzedaż',  vi: 'Để bán',
  },
  forRent: {
    en: 'For Rent',  th: 'เช่า',      cn: '出租',      ru: 'Аренда',       de: 'Mieten',     fr: 'À louer',
    it: 'In affitto', es: 'En alquiler', tr: 'Kiralık', ja: '賃貸',         ko: '임대',        ar: 'للإيجار',
    he: 'להשכרה',    pt: 'Para alugar', nl: 'Te huur', pl: 'Do wynajęcia', vi: 'Cho thuê',
  },
  listing: {
    en: 'listing',   th: 'รายการ',    cn: '房源',      ru: 'объявление',   de: 'Angebot',    fr: 'annonce',
    it: 'annuncio',  es: 'inmueble',  tr: 'ilan',      ja: '物件',          ko: '매물',        ar: 'إعلان',
    he: 'רישום',     pt: 'imóvel',    nl: 'woning',    pl: 'oferta',       vi: 'bất động sản',
  },
  listings: {
    en: 'listings',  th: 'รายการ',    cn: '房源',      ru: 'объявлений',   de: 'Angebote',   fr: 'annonces',
    it: 'annunci',   es: 'inmuebles', tr: 'ilan',      ja: '物件',          ko: '매물',        ar: 'إعلانات',
    he: 'רישומים',   pt: 'imóveis',   nl: 'woningen',  pl: 'ofert',        vi: 'bất động sản',
  },
  noListingsFound: {
    en: 'No listings found',        th: 'ไม่พบรายการ',          cn: '未找到房源',
    ru: 'Объявления не найдены',    de: 'Keine Angebote gefunden', fr: 'Aucune annonce trouvée',
    it: 'Nessun annuncio trovato',  es: 'No se encontraron inmuebles', tr: 'İlan bulunamadı',
    ja: '物件が見つかりません',        ko: '매물을 찾을 수 없습니다',    ar: 'لم يتم العثور على إعلانات',
    he: 'לא נמצאו רישומים',         pt: 'Nenhum imóvel encontrado',   nl: 'Geen woningen gevonden',
    pl: 'Nie znaleziono ofert',     vi: 'Không tìm thấy bất động sản',
  },
  noListingsMsg: {
    en: 'This agent has no {type} listings at this time.',
    th: 'ตัวแทนรายนี้ไม่มีรายการ{type}ในขณะนี้',
    cn: '该经纪人目前没有{type}房源。',
    ru: 'У этого агента нет объявлений{type}в данный момент.',
    de: 'Dieser Makler hat derzeit keine{type}Angebote.',
    fr: 'Cet agent n\'a pas d\'annonces{type}pour le moment.',
    it: 'Questo agente non ha annunci{type}al momento.',
    es: 'Este agente no tiene inmuebles{type}en este momento.',
    tr: 'Bu acentenin şu anda{type}ilanı yok.',
    ja: 'このエージェントには現在{type}物件がありません。',
    ko: '이 에이전트는 현재{type}매물이 없습니다.',
    ar: 'لا توجد إعلانات{type}لهذا الوكيل في الوقت الحالي.',
    he: 'לסוכן זה אין כרגע רישומים{type}.',
    pt: 'Este agente não tem imóveis{type}no momento.',
    nl: 'Deze agent heeft momenteel geen{type}woningen.',
    pl: 'Ten agent nie ma obecnie ofert{type}.',
    vi: 'Đại lý này hiện không có bất động sản{type}.',
  },
  saleType: {
    en: 'sale',   th: 'ขาย',     cn: '出售',   ru: 'на продажу',  de: 'zum Kauf',  fr: 'à vendre',
    it: 'in vendita', es: 'en venta', tr: 'satılık', ja: '売却',   ko: '매매',      ar: 'للبيع',
    he: 'למכירה', pt: 'à venda', nl: 'te koop', pl: 'na sprzedaż', vi: 'để bán',
  },
  rentType: {
    en: 'rental', th: 'เช่า',    cn: '出租',   ru: 'в аренду',    de: 'zur Miete', fr: 'à louer',
    it: 'in affitto', es: 'en alquiler', tr: 'kiralık', ja: '賃貸', ko: '임대',     ar: 'للإيجار',
    he: 'להשכרה', pt: 'para alugar', nl: 'te huur', pl: 'do wynajęcia', vi: 'cho thuê',
  },
  loadMore: {
    en: 'Load More',   th: 'โหลดเพิ่มเติม',  cn: '加载更多',    ru: 'Загрузить ещё',  de: 'Mehr laden',
    fr: 'Charger plus', it: 'Carica altro',  es: 'Cargar más',  tr: 'Daha fazla yükle', ja: 'もっと見る',
    ko: '더 보기',      ar: 'تحميل المزيد',  he: 'טען עוד',     pt: 'Carregar mais',  nl: 'Meer laden',
    pl: 'Załaduj więcej', vi: 'Tải thêm',
  },
}

const l = (key, lang) => UI_LABELS[key]?.[lang] ?? UI_LABELS[key]?.en ?? key

// ── Pagination button ──────────────────────────────────────────────────────────
function PaginationBtn({ label, onClick, active, disabled, activeBg, activeTc, borderColor }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: '6px 12px', borderRadius: 5, fontSize: '0.875rem', fontWeight: active ? 700 : 400,
        border: `1px solid ${active ? activeBg : borderColor}`,
        background: active ? activeBg : '#fff',
        color: active ? activeTc : '#374151',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.4 : 1,
      }}>
      {label}
    </button>
  )
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function AgentListingsCards({
  agentId,
  cardStyle,
  dictionary,
  lang           = 'en',
  searchPageConfig,
  initialSaleType = '',
}) {
  const { data: session } = useSession()
  const userEmail = session?.user?.email ?? null

  const router    = useRouter()
  const pathname  = usePathname()
  const urlParams = useSearchParams()

  const cfg          = searchPageConfig ?? {}
  const scrollMode   = cfg.scrollMode          ?? 'pagination'
  const colsDesktop  = cfg.colsDesktop          ?? 4
  const colsTablet   = cfg.colsTablet           ?? 2
  const colsMobile   = cfg.colsMobile           ?? 1
  const cardGap      = cfg.cardGap              ?? 12
  const btnBg        = cfg.btnBg               ?? 'var(--thirdcolor)'
  const btnTextColor = cfg.btnTextColor         ?? '#ffffff'
  const btnRadius    = cfg.btnRadius            ?? 6
  const pActiveBg    = cfg.paginationActiveBg   ?? 'var(--thirdcolor)'
  const pActiveTc    = cfg.paginationActiveText ?? '#ffffff'
  const pBorderColor = cfg.paginationBorderColor ?? '#e5e7eb'

  const initPage = scrollMode === 'pagination' ? (Number(urlParams.get('page')) || 1) : 1

  const [page,       setPage]       = useState(initPage)
  const [allPages,   setAllPages]   = useState(1)
  const [properties, setProperties] = useState([])
  const [count,      setCount]      = useState(0)
  const [isLoading,  setIsLoading]  = useState(false)
  const [saleType,   setSaleType]   = useState(initialSaleType)

  const propertiesRef = useRef([])
  const initialized   = useRef(false)

  const fetchData = async (pageNum, sale, reset = false) => {
    setIsLoading(true)
    const result = await getAgentProperties(agentId, pageNum, lang, sale, userEmail)
    if (pageNum > 1 && scrollMode !== 'pagination' && !reset) {
      const next = [...propertiesRef.current, ...result.properties]
      propertiesRef.current = next
      setProperties(next)
    } else {
      propertiesRef.current = result.properties
      setProperties(result.properties)
    }
    setAllPages(result.pages)
    setCount(result.count)
    setIsLoading(false)
  }

  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true
      fetchData(initPage, saleType)
    }
  }, [])

  useEffect(() => {
    if (scrollMode === 'pagination') {
      const urlPage = Number(urlParams.get('page')) || 1
      if (urlPage !== page) {
        setPage(urlPage)
        fetchData(urlPage, saleType)
      }
    }
  }, [urlParams])

  const goToPage = (p) => {
    setPage(p)
    if (scrollMode === 'pagination') {
      const params = new URLSearchParams(urlParams.toString())
      params.set('page', String(p))
      router.push(`${pathname}?${params.toString()}`)
    }
    fetchData(p, saleType)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const loadMore = () => {
    const next = page + 1
    setPage(next)
    fetchData(next, saleType)
  }

  const gridCss = `
    .alc-grid {
      display: grid;
      grid-template-columns: repeat(${colsMobile}, 1fr);
      gap: ${cardGap}px;
    }
    @media (min-width: 640px) {
      .alc-grid { grid-template-columns: repeat(${colsTablet}, 1fr); }
    }
    @media (min-width: 1024px) {
      .alc-grid { grid-template-columns: repeat(${colsDesktop}, 1fr); }
    }
  `

  // ✅ Multilingual sale type filter options
  const saleTypeOptions = [
    { value: '',     label: l('all',     lang) },
    { value: 'sale', label: l('forSale', lang) },
    { value: 'rent', label: l('forRent', lang) },
  ]

  const handleSaleType = (val) => {
    setSaleType(val)
    setPage(1)
    propertiesRef.current = []
    setProperties([])
    fetchData(1, val, true)
    if (scrollMode === 'pagination') {
      const params = new URLSearchParams(urlParams.toString())
      params.delete('page')
      router.push(`${pathname}?${params.toString()}`)
    }
  }

  const paginationPages = () => {
    if (allPages <= 7) return Array.from({ length: allPages }, (_, i) => i + 1)
    const pages = new Set([1, allPages, page, page - 1, page + 1].filter(p => p >= 1 && p <= allPages))
    const sorted = [...pages].sort((a, b) => a - b)
    const result = []
    let prev = null
    for (const p of sorted) {
      if (prev && p - prev > 1) result.push('…')
      result.push(p)
      prev = p
    }
    return result
  }

  // ✅ Multilingual empty state — resolves the listing type word into the UI language
  const emptyStateMsg = () => {
    const typeWord = saleType === 'sale'
      ? ` ${l('saleType', lang)} `
      : saleType === 'rent'
        ? ` ${l('rentType', lang)} `
        : ' '
    const template = l('noListingsMsg', lang)
    return template.replace('{type}', typeWord)
  }

  return (
    <div>
      <style>{gridCss}</style>

      {/* ── Filter tabs + count ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <div style={{ display: 'flex', gap: '0.375rem' }}>
          {saleTypeOptions.map(opt => (
            <button key={opt.value} onClick={() => handleSaleType(opt.value)}
              style={{
                padding: '5px 14px', borderRadius: btnRadius, fontSize: '0.8125rem', fontWeight: saleType === opt.value ? 700 : 500,
                border: `1px solid ${saleType === opt.value ? btnBg : '#e5e7eb'}`,
                background: saleType === opt.value ? btnBg : '#fff',
                color: saleType === opt.value ? btnTextColor : '#374151',
                cursor: 'pointer',
              }}>
              {opt.label}
            </button>
          ))}
        </div>
        {/* ✅ Multilingual listing count */}
        {!isLoading && count > 0 && (
          <span style={{ fontSize: '0.8125rem', color: '#6b7280' }}>
            {count} {count === 1 ? l('listing', lang) : l('listings', lang)}
          </span>
        )}
      </div>

      {/* ── Grid ── */}
      {isLoading && properties.length === 0 ? (
        <div className="alc-grid">
          {Array.from({ length: colsDesktop }).map((_, i) => <LoadingCard key={i} />)}
        </div>
      ) : properties.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '4rem 2rem', color: '#9ca3af' }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🏠</div>
          {/* ✅ Multilingual empty state */}
          <p style={{ fontWeight: 600, color: '#6b7280', marginBottom: '0.25rem' }}>{l('noListingsFound', lang)}</p>
          <p style={{ fontSize: '0.875rem' }}>{emptyStateMsg()}</p>
        </div>
      ) : (
        <div className="alc-grid">
          {properties.map((property, i) => (
            <LargePropertyCard
              key={property.id ?? property._id ?? i}
              property={property}
              dictionary={dictionary.card}
              lang={lang}
              cardStyle={cardStyle}
            />
          ))}
          {isLoading && scrollMode !== 'pagination' && (
            Array.from({ length: colsDesktop }).map((_, i) => <LoadingCard key={`load-${i}`} />)
          )}
        </div>
      )}

      {/* ── Load more button ── */}
      {scrollMode === 'loadmore' && page < allPages && !isLoading && (
        <div style={{ textAlign: 'center', marginTop: '2rem' }}>
          {/* ✅ Multilingual load more */}
          <button onClick={loadMore}
            style={{ padding: '10px 32px', borderRadius: btnRadius, background: btnBg, color: btnTextColor, fontWeight: 600, fontSize: '0.9375rem', border: 'none', cursor: 'pointer' }}>
            {l('loadMore', lang)}
          </button>
        </div>
      )}

      {/* ── Infinite scroll sentinel ── */}
      {scrollMode === 'infinite' && page < allPages && !isLoading && (
        <InfiniteScrollSentinel onVisible={loadMore} />
      )}

      {/* ── Pagination ── */}
      {scrollMode === 'pagination' && allPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '0.375rem', marginTop: '2rem', flexWrap: 'wrap' }}>
          <PaginationBtn label={prevArrow(lang)} onClick={() => goToPage(page - 1)} disabled={page === 1} activeBg={pActiveBg} activeTc={pActiveTc} borderColor={pBorderColor} />
          {paginationPages().map((p, i) =>
            p === '…'
              ? <span key={`ellipsis-${i}`} style={{ padding: '6px 4px', color: '#9ca3af', lineHeight: '1.75' }}>…</span>
              : <PaginationBtn key={p} label={String(p)} onClick={() => goToPage(p)} active={p === page} activeBg={pActiveBg} activeTc={pActiveTc} borderColor={pBorderColor} />
          )}
          <PaginationBtn label={nextArrow(lang)} onClick={() => goToPage(page + 1)} disabled={page === allPages} activeBg={pActiveBg} activeTc={pActiveTc} borderColor={pBorderColor} />
        </div>
      )}
    </div>
  )
}

// ── Intersection observer sentinel for infinite scroll ─────────────────────────
function InfiniteScrollSentinel({ onVisible }) {
  const ref = useRef(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const obs = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) onVisible() }, { threshold: 0.1 })
    obs.observe(el)
    return () => obs.disconnect()
  }, [onVisible])
  return <div ref={ref} style={{ height: 40 }} />
}